import { useState, useRef, useCallback, type DragEvent, type ChangeEvent } from 'react';
import { X, CloudArrowUp, FileText, CheckCircle, Spinner, Lock, UsersThree, Buildings, ShareNetwork } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { uploadDocument, type DocumentPermissionScope } from '@/lib/documentsApi';
import { useMattersStore } from '@/lib/mattersStore';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DOCUMENT_ACCEPT_ATTR, DOCUMENT_FORMATS_LABEL } from '@jurivoxa/shared';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Confidentiality tiers, in order of increasing exposure. Wording is the barrister's, not the
 * schema's — "Counsel only" is `barrister_only`, the tier that keeps privileged material away
 * from clerks and admins.
 */
const SCOPES: { value: DocumentPermissionScope; label: string; sub: string; Icon: typeof Lock }[] = [
  { value: 'barrister_only', label: 'Counsel only',      sub: 'Lead and junior counsel on this matter. Clerks and admins cannot see it.', Icon: Lock },
  { value: 'case_team',      label: 'Case team',         sub: 'Counsel and the clerk assigned to this matter.',                            Icon: UsersThree },
  { value: 'chambers',       label: 'Chambers',          sub: 'All chambers staff. Never the instructing solicitor.',                      Icon: Buildings },
  { value: 'client_visible', label: 'Share with client', sub: 'Also visible to the instructing solicitor in their portal.',                Icon: ShareNetwork },
];

// Label → DB enum value (DocumentCategory)
const DOC_TYPES: { label: string; value: string }[] = [
  { label: 'Brief',                 value: 'brief'          },
  { label: 'Skeleton argument',     value: 'skeleton'       },
  { label: 'Bundle of authorities', value: 'authorities'    },
  { label: 'Hearing bundle',        value: 'bundle'         },
  { label: 'Pleading',              value: 'pleading'       },
  { label: 'Witness statement',     value: 'evidence'       },
  { label: 'Expert report',         value: 'evidence'       },
  { label: 'Submission',            value: 'pleading'       },
  { label: 'Opinion',               value: 'opinion'        },
  { label: 'Correspondence',        value: 'correspondence' },
  { label: 'Order',                 value: 'order'          },
  { label: 'Judgment',              value: 'judgment'       },
  { label: 'Other',                 value: 'other'          },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  open: boolean;
  /** Pre-select a matter (e.g. when uploading from inside a matter page) */
  defaultCaseId?: string;
  /** Pin the upload to a specific hearing — document appears in the hearing's bundle */
  defaultHearingId?: string;
  onClose: () => void;
  onUpload: () => void;
}

export function UploadDocumentModal({ open, defaultCaseId, defaultHearingId, onClose, onUpload }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<{ label: string; value: string }>(DOC_TYPES[0]!);
  const [caseId, setCaseId] = useState(defaultCaseId ?? '');
  const [scope, setScope] = useState<DocumentPermissionScope>('case_team');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [complete, setComplete] = useState(false);

  const matters = useMattersStore((s) => s.matters);
  const matterOptions = matters.map((m) => ({ value: m.id, label: `${m.number} · ${m.name}` }));

  // A matter reaches this modal either from the page it was opened on or from the picker.
  const effectiveCaseId = defaultCaseId ?? caseId;

  // useCallback must be before the early return (Rules of Hooks)
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      await uploadDocument(selectedFile, {
        caseId: effectiveCaseId || undefined,
        category: docType.value,
        hearingId: defaultHearingId,
        // Only meaningful with a matter — the API rejects a counsel/case-team tier without one.
        permissionScope: effectiveCaseId ? scope : undefined,
      });
      setComplete(true);
      toast.success(`"${selectedFile.name}" uploaded`);
      onUpload();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? (err as Error)?.message
        ?? 'Unknown error';
      console.error('Upload failed:', err);
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  }, [selectedFile, effectiveCaseId, docType, scope, defaultHearingId, onUpload, onClose]);

  if (!open) return null;

  const handleFile = (file: File) => {
    setSelectedFile(file);
    setComplete(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const canUpload = !!selectedFile && !uploading;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <Card className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <div className="text-sm font-semibold tracking-tight">Upload document</div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground">
            <X size={16} weight="bold" />
          </button>
        </div>

        <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          {/* Drop zone */}
          <div
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 transition ${
              dragOver ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-border hover:border-brand-400 hover:bg-muted/30'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {selectedFile ? (
              <div className="flex items-center gap-3 text-center">
                <FileText size={28} weight="duotone" className="shrink-0 text-brand-600 dark:text-brand-400" />
                <div>
                  <div className="text-sm font-semibold text-foreground">{selectedFile.name}</div>
                  <div className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</div>
                </div>
              </div>
            ) : (
              <>
                <CloudArrowUp size={32} weight="duotone" className="mb-2 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Drop file here or click to browse</p>
                <p className="mt-1 text-xs text-muted-foreground">{DOCUMENT_FORMATS_LABEL} accepted</p>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleInputChange} accept={DOCUMENT_ACCEPT_ATTR} />

          {/* Matter — hidden from hearings, read-only when opened from a matter page, dropdown otherwise */}
          {!defaultHearingId && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Matter {!defaultCaseId && <span className="font-normal normal-case">(optional)</span>}
              </label>
              {defaultCaseId ? (
                <div className="flex items-center rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                  {matterOptions.find((m) => m.value === defaultCaseId)?.label ?? defaultCaseId}
                </div>
              ) : (
                <SearchableSelect
                  value={caseId}
                  onChange={setCaseId}
                  placeholder="Link to a matter…"
                  options={[{ value: '', label: 'No matter (general upload)' }, ...matterOptions]}
                />
              )}
            </div>
          )}

          {/* Document type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Document type</label>
            <div className="flex flex-wrap gap-1.5">
              {DOC_TYPES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setDocType(t)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition ${
                    docType.label === t.label ? 'border-brand-600 bg-brand-600 text-white' : 'border-border text-muted-foreground hover:border-brand-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/*
            Confidentiality tier. Only offered when the document is linked to a matter, because
            the tier resolves against that matter's counsel assignment — an unlinked document
            has nothing to resolve against and the API refuses anything but "chambers".

            This control is the write path for `barrister_only`. Without it the API silently
            stored every document as `case_team`, so counsel's strategy material sat in the
            clerk-readable tier and there was no way for a barrister to mark anything privileged.
          */}
          {effectiveCaseId && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Who can see this
              </label>
              <div className="space-y-1.5">
                {SCOPES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setScope(s.value)}
                    className={`flex w-full items-start gap-2.5 rounded-md border px-3 py-2 text-left transition ${
                      scope === s.value ? 'border-brand-600 bg-brand-50 dark:bg-brand-600/10' : 'border-border hover:border-brand-300'
                    }`}
                  >
                    <s.Icon className={`mt-0.5 h-4 w-4 shrink-0 ${scope === s.value ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{s.label}</span>
                      <span className="block text-xs text-muted-foreground">{s.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button size="sm" onClick={handleUpload} disabled={!canUpload}>
            {uploading ? (
              <><Spinner size={13} className="mr-1.5 animate-spin" />Uploading…</>
            ) : complete ? (
              <><CheckCircle size={13} className="mr-1.5" />Done</>
            ) : (
              <><CloudArrowUp size={13} className="mr-1.5" />Upload</>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
